import React from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const Education = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguageContext();
  const t = translations[language];

  const EducationCard = ({ item, isLast }) => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Typography variant="h6" component="h3" color="primary" gutterBottom>
        {item.title}
      </Typography>

      {item.institution && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <BusinessIcon 
            sx={{ 
              mr: 1.5, 
              width: { xs: '1.5rem', sm: '2rem' },
              height: { xs: '1.5rem', sm: '2rem' },
              color: 'primary.main',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'rotate(10deg) scale(1.1)',
              },
            }} 
          />
          <Typography variant="subtitle1" color="text.secondary">
            {item.institution}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CalendarTodayIcon 
          sx={{ 
            mr: 1.5, 
            width: { xs: '1.5rem', sm: '2rem' },
            height: { xs: '1.5rem', sm: '2rem' },
            color: 'primary.main',
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'rotate(10deg) scale(1.1)',
            },
          }} 
        />
        <Typography variant="subtitle2" color="text.secondary">
          {item.period}
        </Typography>
      </Box>

      {item.location && (
        <Chip
          label={item.location}
          color="primary"
          variant="outlined"
          size="small"
          sx={{
            mb: 2,
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        />
      )}

      {item.description && (
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'justify',
            transition: 'color 0.2s ease-in-out',
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          {item.description}
        </Typography>
      )}
      {!isLast && isMobile && <Divider sx={{ mt: 3 }} />}
    </Paper>
  );

  return (
    <Box sx={{ py: 6 }}>
      <Box 
        sx={{ 
          mb: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            fontWeight: 'bold'
          }}
        >
          {isMobile && (
            <SchoolIcon 
              sx={{ 
                color: 'primary.main',
                width: { xs: '1.5rem', sm: '2rem' },
                height: { xs: '1.5rem', sm: '2rem' },
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'rotate(10deg) scale(1.1)',
                },
              }} 
            />
          )}
          {t.about.education.title}
        </Typography>
      </Box>

      {isMobile ? (
        <Stack spacing={3}>
          {t.about.education.items.map((item, index) => (
            <EducationCard 
              key={index} 
              item={item} 
              isLast={index === t.about.education.items.length - 1}
            />
          ))}
        </Stack>
      ) : (
        <Timeline position="alternate-reverse">
          {t.about.education.items.map((item, index) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    bgcolor: 'primary.main',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.2) rotate(10deg)',
                    },
                  }}
                >
                  <SchoolIcon />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <EducationCard item={item} />
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
};

export default Education;
